import { useMemo, useState } from "react";
import {
  AlertCircle,
  Box,
  Circle,
  CircleDot,
  Cone,
  Copy,
  Eye,
  EyeOff,
  GitBranch,
  Grid3X3,
  ListTree,
  MoreHorizontal,
  Plus,
  Power,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Triangle
} from "lucide-react";
import type { FeatureNode, NodeType } from "../model/types";
import { useModelStore } from "../state/store";
import { ExpressionInput, Field, NumberInput, Section, TextInput, Toggle } from "./Fields";

type PanelTab = "design" | "parameters" | "motives";

const icons: Record<NodeType, React.JSX.Element> = {
  box: <Box size={15} />,
  cylinder: <CircleDot size={15} />,
  sphere: <Circle size={15} />,
  cone: <Cone size={15} />,
  wedge: <Triangle size={15} />,
  transform: <MoreHorizontal size={15} />,
  boolean: <GitBranch size={15} />,
  hole: <CircleDot size={15} />,
  fillet: <Sparkles size={15} />,
  chamfer: <Triangle size={15} />,
  mirror: <Copy size={15} />,
  linearPattern: <Grid3X3 size={15} />,
  circularPattern: <Grid3X3 size={15} />
};

export function DesignPanel(): React.JSX.Element {
  const [tab, setTab] = useState<PanelTab>("design");
  return (
    <aside className="design-panel">
      <div className="panel-tabs" role="tablist">
        <button className={tab === "design" ? "active" : ""} onClick={() => setTab("design")} title="Design tree"><ListTree size={16} /><span>Design</span></button>
        <button className={tab === "parameters" ? "active" : ""} onClick={() => setTab("parameters")} title="Parameters"><SlidersHorizontal size={16} /><span>Parameters</span></button>
        <button className={tab === "motives" ? "active" : ""} onClick={() => setTab("motives")} title="Motive authoring"><Sparkles size={16} /><span>Motives</span></button>
      </div>
      <div className="panel-scroll">
        {tab === "design" && <FeatureTree />}
        {tab === "parameters" && <ParameterEditor />}
        {tab === "motives" && <MotiveAuthor />}
      </div>
    </aside>
  );
}

function FeatureTree(): React.JSX.Element {
  const nodes = useModelStore((state) => state.document.nodes);
  const selected = useModelStore((state) => state.selectedNodeIds);
  const diagnostics = useModelStore((state) => state.diagnostics);
  const selectNode = useModelStore((state) => state.selectNode);
  const toggleVisibility = useModelStore((state) => state.toggleNodeVisibility);
  const duplicate = useModelStore((state) => state.duplicateSelected);
  const deleteSelected = useModelStore((state) => state.deleteSelected);
  const addPrimitive = useModelStore((state) => state.addPrimitive);

  const childCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const node of nodes) for (const input of node.inputNodeIds) counts.set(input, (counts.get(input) ?? 0) + 1);
    return counts;
  }, [nodes]);

  return (
    <>
      <div className="panel-heading">
        <div><span className="eyebrow">Feature history</span><strong>{nodes.length} feature{nodes.length === 1 ? "" : "s"}</strong></div>
        <button className="icon-button" title="Add box" onClick={() => addPrimitive("box")}><Plus size={16} /></button>
      </div>
      {nodes.length === 0 ? (
        <div className="panel-empty">
          <Box size={26} />
          <h3>Start with a solid</h3>
          <p>Add a box, cylinder, sphere, cone, or wedge.</p>
          <button className="button primary" onClick={() => addPrimitive("box")}><Plus size={15} /> Add box</button>
        </div>
      ) : (
        <div className="feature-tree" role="tree">
          {nodes.map((node, index) => {
            const selectedNode = selected.includes(node.id);
            const failed = diagnostics.some((diagnostic) => diagnostic.nodeId === node.id && diagnostic.severity === "error");
            return (
              <div
                className={`feature-row ${selectedNode ? "selected" : ""} ${node.enabled ? "" : "suppressed"}`}
                role="treeitem"
                aria-selected={selectedNode}
                key={node.id}
                onClick={(event) => selectNode(node.id, event.ctrlKey || event.metaKey || event.shiftKey)}
              >
                <span className="history-index">{index + 1}</span>
                <span className="feature-icon" style={{ color: node.color }}>{failed ? <AlertCircle size={15} /> : icons[node.type]}</span>
                <span className="feature-row-main"><strong>{node.name}</strong><small>{featureSummary(node)}{(childCount.get(node.id) ?? 0) > 1 ? ` · ${childCount.get(node.id)} branches` : ""}</small></span>
                {!node.enabled && <Power size={13} className="muted" />}
                <button className="tree-action" title={node.visible ? "Hide" : "Show"} onClick={(event) => { event.stopPropagation(); toggleVisibility(node.id); }}>{node.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
              </div>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div className="panel-footer-actions">
          <button onClick={duplicate} disabled={selected.length !== 1}><Copy size={14} /> Duplicate</button>
          <button className="danger-text" onClick={deleteSelected}><Trash2 size={14} /> Delete</button>
        </div>
      )}
    </>
  );
}

function ParameterEditor(): React.JSX.Element {
  const parameters = useModelStore((state) => state.document.parameters);
  const exposed = useModelStore((state) => state.document.exposedParameters);
  const addParameter = useModelStore((state) => state.addParameter);
  const removeParameter = useModelStore((state) => state.removeParameter);
  const updateParameter = useModelStore((state) => state.updateParameter);
  const updateParameterValue = useModelStore((state) => state.updateParameterValue);
  const exposeParameter = useModelStore((state) => state.exposeParameter);
  const entries = Object.values(parameters);

  return (
    <>
      <div className="panel-heading">
        <div><span className="eyebrow">Named dimensions</span><strong>Parameters</strong></div>
        <button className="icon-button" title="Add parameter" onClick={addParameter}><Plus size={16} /></button>
      </div>
      <p className="panel-intro">Use parameter names inside any dimension, for example <code>width - wall * 2</code>.</p>
      {entries.length === 0 ? (
        <div className="panel-empty compact"><SlidersHorizontal size={24} /><h3>No parameters yet</h3><p>Create reusable named dimensions for parametric models.</p><button className="button primary" onClick={addParameter}><Plus size={15} /> Add parameter</button></div>
      ) : entries.map((parameter) => {
        const isExposed = exposed.some((entry) => entry.parameterId === parameter.id);
        return (
          <Section
            key={parameter.id}
            title={parameter.label || parameter.name}
            actions={<button className="icon-button tiny danger-ghost" title="Delete parameter" onClick={() => removeParameter(parameter.id)}><Trash2 size={13} /></button>}
          >
            <Field label="Label"><TextInput value={parameter.label} onChange={(label) => updateParameter(parameter.id, { label })} /></Field>
            <Field label="Name"><TextInput value={parameter.name} onChange={(name) => updateParameter(parameter.id, { name })} /></Field>
            <Field label="Value"><ExpressionInput value={parameter.value} onChange={(value) => updateParameterValue(parameter.id, value)} /></Field>
            <Field label="Kind"><select value={parameter.type} onChange={(event) => updateParameter(parameter.id, { type: event.target.value })}><option value="length">Length</option><option value="angle">Angle</option><option value="integer">Integer</option></select></Field>
            <div className="two-column-fields">
              <Field label="Min"><NumberInput value={parameter.minimum} onChange={(minimum) => updateParameter(parameter.id, { minimum })} /></Field>
              <Field label="Max"><NumberInput value={parameter.maximum} onChange={(maximum) => updateParameter(parameter.id, { maximum })} /></Field>
            </div>
            <Field label="Expose"><Toggle checked={isExposed} onChange={(checked) => exposeParameter(parameter.id, checked)} label="Expose in Motive mode" /></Field>
          </Section>
        );
      })}
    </>
  );
}

function MotiveAuthor(): React.JSX.Element {
  const document = useModelStore((state) => state.document);
  const exposeParameter = useModelStore((state) => state.exposeParameter);
  const updateExposed = useModelStore((state) => state.updateExposedParameter);
  const setMode = useModelStore((state) => state.setMode);
  const parameters = Object.values(document.parameters);

  return (
    <>
      <div className="panel-heading"><div><span className="eyebrow">Curated controls</span><strong>Motive authoring</strong></div></div>
      <p className="panel-intro">Expose only the dimensions another person should change. The full feature history remains editable.</p>
      {parameters.length === 0 && <div className="panel-empty compact"><Sparkles size={24} /><h3>Add parameters first</h3><p>Motives are powered by named parameters.</p></div>}
      {parameters.map((parameter) => {
        const item = document.exposedParameters.find((entry) => entry.parameterId === parameter.id);
        return (
          <Section key={parameter.id} title={parameter.label} actions={<Toggle checked={Boolean(item)} onChange={(checked) => exposeParameter(parameter.id, checked)} label={`Expose ${parameter.label}`} />}>
            {item ? (
              <>
                <Field label="Public label"><TextInput value={item.label} onChange={(label) => updateExposed(parameter.id, { label })} /></Field>
                <Field label="Group"><TextInput value={item.group} onChange={(group) => updateExposed(parameter.id, { group })} /></Field>
                <div className="two-column-fields">
                  <Field label="Min"><NumberInput value={item.minimum} onChange={(minimum) => updateExposed(parameter.id, { minimum })} /></Field>
                  <Field label="Max"><NumberInput value={item.maximum} onChange={(maximum) => updateExposed(parameter.id, { maximum })} /></Field>
                </div>
                <Field label="Step"><NumberInput value={item.step} min={0.001} onChange={(step) => updateExposed(parameter.id, { step: step ?? 1 })} /></Field>
              </>
            ) : <p className="section-note">Hidden from Motive use mode.</p>}
          </Section>
        );
      })}
      {document.exposedParameters.length > 0 && <button className="button primary full preview-motive" onClick={() => setMode("motive")}><Sparkles size={15} /> Preview Motive</button>}
    </>
  );
}

function featureSummary(node: FeatureNode): string {
  switch (node.type) {
    case "box": return `${node.width} × ${node.depth} × ${node.height} mm`;
    case "cylinder": return `R ${node.radius} · H ${node.height}`;
    case "sphere": return `R ${node.radius}`;
    case "cone": return `${node.bottomRadius} → ${node.topRadius} · H ${node.height}`;
    case "wedge": return `${node.width} × ${node.depth} × ${node.height}`;
    case "boolean": return node.operation;
    case "hole": return `R ${node.radius} · ${node.axis} axis`;
    case "fillet": return `R ${node.radius} · ${node.selection}`;
    case "chamfer": return `${node.distance} mm · ${node.selection}`;
    case "mirror": return `${node.plane} plane`;
    case "linearPattern": return `${node.count} × ${node.spacing} mm`;
    case "circularPattern": return `${node.count} × ${node.totalAngle}°`;
    case "transform": return `Move ${node.translation.join(", ")}`;
  }
}
